import React, { useState, useEffect } from 'react';
import './FaqButton.css';

const FaqButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const faqs = [
    {
      question: "What is feelr?",
      answer: "feelr is an AI-powered wellbeing solution designed to support you in improving your emotional granularity."
    },
    {
      question: "What is emotional granularity?",
      answer: "Emotional granularity represents the detail with which people describe their emotional experiences.<sup><a href=\"https://compass.onlinelibrary.wiley.com/doi/abs/10.1111/spc3.12584\" target=\"_blank\" rel=\"noopener noreferrer\">1</a></sup>"
    },
    {
      question: "How can emotional granularity benefit me today?",
      answer: "Granular, context-sensitive processing helps you take the right actions based on your situation.<sup><a href=\"https://journals.sagepub.com/doi/10.1177/0963721414550708\" target=\"_blank\" rel=\"noopener noreferrer\">2</a>, <a href=\"https://journals.sagepub.com/doi/10.1177/1745691612459518\" target=\"_blank\" rel=\"noopener noreferrer\">3</a></sup> Saying you feel ‘off’ doesn’t provide a clear path forward, but recognizing that you’re actually ‘lonely,’ ‘tired,’ or ‘fidgety’ can suggest meaningful next steps."
    },
    {
      question: "How can emotional granularity benefit me in the long run?",
      answer: "Research shows that greater emotional granularity—especially with unpleasant emotions—is linked to better emotional regulation and mental health.<sup><a href=\"https://www.tandfonline.com/doi/abs/10.1080/02699930143000239\" target=\"_blank\" rel=\"noopener noreferrer\">4</a>, <a href=\"https://journals.sagepub.com/doi/10.1177/2167702617694657\" target=\"_blank\" rel=\"noopener noreferrer\">5</a>, <a href=\"https://www.sciencedirect.com/science/article/abs/pii/S2352250X15000986\" target=\"_blank\" rel=\"noopener noreferrer\">6</a></sup><p class=\"mt-4\">As an example, people who can more precisely identify how they feel are less likely to engage in maladaptive coping (like binge drinking) and tend to experience fewer symptoms of depression and anxiety.<sup><a href=\"https://journals.sagepub.com/doi/10.1177/0956797610379863\" target=\"_blank\" rel=\"noopener noreferrer\">7</a>, <a href=\"https://onlinelibrary.wiley.com/doi/10.1111/jopy.12475\" target=\"_blank\" rel=\"noopener noreferrer\">8</a>, <a href=\"https://www.sciencedirect.com/science/article/abs/pii/S0005789419300668?via%3Dihub\" target=\"_blank\" rel=\"noopener noreferrer\">9</a></sup></p><p class=\"mt-4\">Getting granular with pleasant emotions has been associated with greater psychological resilience.<sup><a href=\"https://onlinelibrary.wiley.com/doi/10.1111/j.1467-6494.2004.00294.x\" target=\"_blank\" rel=\"noopener noreferrer\">10</a></sup>"
    },
    {
      question: "How does emotional granularity compare to other emotion regulation strategies?",
      answer: "Research shows that putting feelings into words can be more effective than other common emotion regulation strategies in reducing emotional arousal and behavioural reactions.<sup><a href=\"https://journals.sagepub.com/doi/10.1177/0956797612443830\" target=\"_blank\" rel=\"noopener noreferrer\">11</a></sup><p class=\"mt-4\">Neuroimaging studies also suggest that the mere presence or absence of feeling words change emotional brain activity in a consistent way.<sup><a href=\"https://academic.oup.com/scan/article/12/2/169/2544460\" target=\"_blank\" rel=\"noopener noreferrer\">12</a></sup></p>"
    },
    {
      question: "Is emotional granularity always beneficial?",
      answer: "Not always. In situations that require quick decisions for safety, a broad sense of an emotion is likely more useful."
    },
    {
      question: "Can emotional granularity be learned?",
      answer:
        "Yes, growing research suggests that emotional granularity is a skill that can be acquired and improved.<sup><a href=\"https://compass.onlinelibrary.wiley.com/doi/abs/10.1111/spc3.12584\" target=\"_blank\" rel=\"noopener noreferrer\">13</a>, <a href=\"https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2021.704125/full\" target=\"_blank\" rel=\"noopener noreferrer\">14</a>, <a href=\"https://journals.lww.com/hrpjournal/abstract/2014/05000/changes_in_alexithymia_following_psychological.3.aspx\" target=\"_blank\" rel=\"noopener noreferrer\">15</a></sup> " +
        "Emotional granularity varies not only between individuals but also within a person over time, indicating that it is malleable.<sup><a href=\"https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2021.704125/full#ref92\" target=\"_blank\" rel=\"noopener noreferrer\">16</a></sup>" +
        "<p class=\"mt-4\">Psychological interventions show that people can learn to construct more granular emotional experiences.<sup><a href=\"https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2021.703757/full\" target=\"_blank\" rel=\"noopener noreferrer\">17</a></sup> In particular, interventions that cultivate emotional awareness and encourage emotion word learning have been shown to improve emotional granularity and support psychological wellbeing.<sup><a href=\"https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0299540\" target=\"_blank\" rel=\"noopener noreferrer\">18</a></sup></p>" +
        "<p class=\"mt-4\"></p>" +
        " At the heart of these interventions is exactly what feelr helps with—expanding a person\’s emotion vocabulary.<sup><a href=\"https://journals.sagepub.com/doi/10.1177/0963721414550708\" target=\"_blank\" rel=\"noopener noreferrer\">19</a>, <a href=\"https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2021.703658/full\" target=\"_blank\" rel=\"noopener noreferrer\">20</a></sup>"
    },
    {
      question: "How does feelr work?",
      answer: "feelr is designed to help you identify one feeling per conversation, drawing from a research-backed knowledge base and an extensive emotion vocabulary. Its chatbot component is powered by Claude 3.5 Sonnet, developed by Anthropic."
    },
    {
      question: "How is feelr different from Claude?",
      answer: "feelr is more than a chatbot—it\’s a specialized tool for emotional granularity. While its chatbot component uses Anthropic\’s Claude 3.5 Sonnet, feelr connects to it via an API and is customized with thorough guidelines (called a system prompt). In addition, while Claude converses with users in an open domain, feelr is a task-focused chatbot that helps you identify nuanced feelings within a limited number of conversation turns."
    },
    {
      question: "What can I expect feelr to do?",
      answer: "feelr can help you get granular by distinguishing between similar feelings, such as recognizing whether you're ‘annoyed‘ or just ‘peeved‘. It can also help you differentiate between feelings with different valence and arousal, like determining if you're truly ‘angry‘ or perhaps ‘lonely‘.<p class=\"mt-4\">Once you identify a feeling, you uncover its shape, which then becomes part of your personalized feelings map.</p>"
    },
    {
      question: "What are the different colours of the feeling shapes?",
      answer: "The colours represent different categories of arousal and valence: red for high-arousal unpleasant, yellow for high-arousal pleasant, blue for low-arousal unpleasant, and purple for low-arousal pleasant feelings."
    },
    {
      question: "But I have multiple feelings at once?!",
      answer: "That\’s normal! We can have multiple feelings at the same time (tired and wired, anyone?). feelr currently helps identify one feeling per conversation—but you’re always welcome to start a new one anytime."
    },
    {
      question: "I just relogged, and my feelings map is gone?!",
      answer: "That\’s a feature, not a bug. We want you to focus on identifying your current feeling first. Rest assured that your feelings map will be available again once you\’ve done that."
    },
    {
      question: "How is feelr different from a Feelings Wheel?",
      answer: "feelr offers a more active way to develop emotional granularity than simply being presented with emotion words—it allows you to share personally relevant experiences and encourages you to observe and label your emotions in greater depth. It also helps you create a personalized feelings map that you can track over time."
    },
    {
      question: "Can feelr state something untrue?",
      answer: "feelr does not provide prescriptive advice, medical diagnosis, or mental health guidance and will not take on that role, even if prompted. However, even with precautions in place to minimize inaccuracies, it is important to know that AI models can sometimes generate incorrect information."
    },
    {
      question: "Does feelr use my conversations to improve?",
      answer: "No, feelr\’s chatbot component does not learn or improve from your conversations. Anonymized transcripts may be reviewed by a human researcher for quality assurance, safety, and improvement. You can opt out by deleting your conversations."
    },
    {
      question: "How does feelr prioritize ethics and safety?",
      answer: "<p class=\"mt-4\">feelr was developed with consideration of the World Health Organization’s (WHO) 2024 guidance on Ethics and Governance of Artificial Intelligence for Health.<sup><a href=\"https://www.who.int/publications/i/item/9789240084759\" target=\"_blank\" rel=\"noopener noreferrer\">21</a></sup></p>" +
              "<p class=\"mt-4\">feelr has built-in guardrails to ensure safe and ethical use. If needed, you have the option to flag a conversation for review. Additionally, Anthropic has a set of safeguards in place to prevent model misuse and does not use feelr conversations to train its models.<sup><a href=\"https://privacy.anthropic.com/en/articles/7996875-can-you-delete-data-that-i-sent-via-api\" target=\"_blank\" rel=\"noopener noreferrer\">22</a></sup> For added safety, feelr also limits the number of conversation turns to maintain a controlled and focused interaction.</p>"
    },
    {
      question: "How does feelr prioritize privacy?",
      answer: "Your conversations remain private between you and feelr, and you can delete a feeling shape—along with its entire conversation—at any time with a single click. To maintain high standards, researchers may conduct quality checks using anonymized conversation records."
    },
    {
      question: "Does feelr recognize feeling words from multiple languages?",
      answer: "feelr’s knowledge base includes some words from other languages but is primarily built on English-language feeling words.<p class=\"mt-4\">Because democratizing AI means welcoming more voices and perspectives—and since every feeling word offers a new way to shape your emotional experience—we invite you to suggest words with just one click.</p>"
    },
    {
      question: "Is feelr a medical device?",
      answer: "No. feelr has no medical purpose and is not a medical device as defined in the 2025 guidance issued by the Medicines and Healthcare Products Regulatory Agency (MHRA).<sup><a href=\"https://assets.publishing.service.gov.uk/media/67a08ff6c496e5d3ddafb6c4/2025.01.29_MHRA_guidance_on_DMHT_-_Device_characterisation__regulatory_qualification_and_classification.pdf\" target=\"_blank\" rel=\"noopener noreferrer\">23</a></sup>"
    },
    {
      question: "Does feelr target clinical conditions?",
      answer: "No. feelr does not target clinical conditions or symptoms that could be considered diagnosable or clinically relevant."
    },
    {
      question: "Where can I share my feedback on feelr?",
      answer: "You can flag a conversation for review or suggest a feeling, both with written input. Alternatively, you can contact us at hello at feelr.ai. As a small team running this demo, we appreciate your patience as we continue refining feelr!"
    }
  ];

  // Handle escape key to close FAQ
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setExpandedIndex(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Reset expanded state whenever FAQ is closed
  useEffect(() => {
    if (!isOpen) {
      setExpandedIndex(null);
    }
  }, [isOpen]);

  const handleFaqClick = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <>
      <button 
        className={`floating-button faq ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle FAQ"
      >
        ?
      </button>
      
      {isOpen && (
        <div className="faq-overlay" onClick={() => {
          setIsOpen(false);
          setExpandedIndex(null);
        }}>
          <div className="faq-panel" onClick={(e) => e.stopPropagation()}>
            <button 
              type="button"
              className="close-button"
              onClick={() => {
                setIsOpen(false);
                setExpandedIndex(null);
              }}
              aria-label="Close FAQ"
            >
              ×
            </button>
            <h3>FAQ</h3>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className={`faq-item ${expandedIndex === index ? 'expanded' : ''}`}
                  onClick={() => handleFaqClick(index)}
                >
                  <div className="faq-question">
                    {faq.question}
                    <span className="faq-arrow">›</span>
                  </div>
                  <div 
                    className="faq-answer" 
                    dangerouslySetInnerHTML={{ __html: faq.answer }} 
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FaqButton;